import * as React from "react";
import { useTranslation } from "react-i18next";

import { ModelCardInterface } from "@dashart/dashart-gpt-shared-library";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Panel } from "primereact/panel";

export interface ModelDetailsProps {
    /**
     * Custom class names
     */
    className?: string;

    /**
     * Model to display
     */
    model: ModelCardInterface;
}

const ModelDetails: React.FunctionComponent<ModelDetailsProps> = (props: ModelDetailsProps) => {
    const { t } = useTranslation();
    const {
        className,
        model
    } = props;

    const basicInformation = useMemo(() => {
        return <div>
            <h3 className={'mb-2'}>
                {model.displayName ? `${model.displayName} (${model.id})` : model.id}
            </h3>

            {model.description && (
                <div className={'mb-3'}>
                    <ReactMarkdown>{model.description ?? ''}</ReactMarkdown>
                </div>
            )}
        </div>
    }, [model]);

    const contextWindow = useMemo(() => {
        if (!model.contextWindow) {
            return null;
        }

        // format by thousand
        const formattedContextWindow = model.contextWindow.toLocaleString();

        return <li className={'mb-2'}>
            {t('chat_screen.model_details.context_window')}: {formattedContextWindow}
        </li>
    }, [model]);

    const abilities = useMemo(() => {
        return <ul className={'list-none p-0 mb-3'}>
            <li className={'mb-2'}>
                <strong>{t('chat_screen.model_details.abilities')}:</strong>
            </li>
            {contextWindow}
            <li className={'mb-2'}>
                {t('chat_screen.model_details.supports_vision')}: {model.supportsVision ? t('chat_screen.model_details.yes') : t('chat_screen.model_details.no')}
            </li>
            <li className={'mb-2'}>
                {t('chat_screen.model_details.supports_system_prompts')}: {model.supportsSystemPrompt ? t('chat_screen.model_details.yes') : t('chat_screen.model_details.no')}
            </li>
        </ul>
    }, [model]);

    const pricing = useMemo(() => {
        const totalCostPerToken = (model.costPerInputToken ?? 0) + (model.costPerOutputToken ?? 0) + (model.costPerReasoningToken ?? 0);

        // if the total cost per token is 0, we do not need to display the pricing
        if (totalCostPerToken === 0) {
            return null;
        }

        return <ul className={'list-none p-0 mb-3'}>
            <li className={'mb-2'}>
                <strong>{t('chat_screen.model_details.pricing')}:</strong>
            </li>
            <li className={'mb-2'}>
                {t('chat_screen.model_details.input_tokens', { inputTokens: model.costPerInputToken ?? 0 })}
            </li>
            <li className={'mb-2'}>
                {t('chat_screen.model_details.output_tokens', { outputTokens: model.costPerOutputToken ?? 0 })}
            </li>
            <li className={'mb-2'}>
                {t('chat_screen.model_details.reasoning_tokens', { reasoningTokens: model.costPerReasoningToken ?? 0 })}
            </li>
        </ul>
    }, [model]);

    return <Panel header={t('chat_screen.model_details.basic_information')} className={`w-12 m-auto sm:w-10 xl:w-8`}>
        {basicInformation}

        <div className={'flex'}>
            <div className={'flex-1'}>
                {abilities}
            </div>
            <div className={'flex-1'}>
                {pricing}
            </div>
        </div>
        </Panel>
}

export default ModelDetails;